<!-- @components/messenger/chat/desktop/LayoutCore.svelte -->
<script>
  import { ZenSplit, ZenPane } from '@layout/zen';
  import { faceStore } from '$lib/stores/face-store';

  export let option = {
    main: { mode: 'fixed', lock: true },
    body: { mode: 'fixed', lock: true },
    content: { mode: 'fixed', lock: true }
  };

  const { rightVisible } = faceStore;

  // ===== KEYS =====
  $: mainKey = option?.mainKey || 'zenchat.main-default';
  $: bodyKey = option?.bodyKey || 'zenchat.body-default';
  $: contentKey = option?.contentKey || 'zenchat.content-default';
  $: layoutPersist = option?.persistLayout;

  // ===== MODE + LOCK DEFAULT (NEW) =====
  $: mainMode = option?.main?.mode ?? option?.mode ?? 'fixed';
  $: mainLock = option?.main?.lock ?? true;

  $: bodyMode = option?.body?.mode ?? option?.mode ?? 'fixed';
  $: bodyLock = option?.body?.lock ?? true;

  $: contentMode = option?.content?.mode ?? option?.mode ?? 'fixed';
  $: contentLock = option?.content?.lock ?? true;

  $: mainNoGrip = option?.main?.noGrip ?? option?.noGrip ?? true;
  $: bodyNoGrip = option?.body?.noGrip ?? option?.noGrip ?? true;
  $: contentNoGrip = option?.content?.noGrip ?? option?.noGrip ?? true;

  $: splitTheme = option?.splitTheme ?? 'clean';
  $: gapColor = option?.gapColor ?? '#f8fafc';
  $: dividerColor = option?.dividerColor ?? '#ccc';
  $: dividerHoverColor = option?.dividerHoverColor ?? 'rgba(148, 163, 184, 0.6)';
  $: dividerSize = option?.dividerSize ?? '2px';
  $: splitterHitSize = option?.splitterHitSize ?? '12px';
  $: gripPosition = option?.gripPosition ?? 'center';
  $: resizeCursor = option?.resizeCursor ?? 'auto';
  $: horizontalCursor = resizeCursor === 'auto' ? 'row-resize' : resizeCursor;
  $: verticalCursor = resizeCursor === 'auto' ? 'col-resize' : resizeCursor;
  $: paneBackground = option?.paneBackground ?? 'rgba(255, 255, 255, 0.78)';
  $: paneBorderColor = option?.paneBorderColor ?? 'transparent';
  $: paneBorderWidth = option?.paneBorderWidth ?? '0px';
  $: contentBackground = option?.contentBackground ?? 'transparent';
  $: contentBackgroundSize = option?.contentBackgroundSize ?? 'cover';
  $: contentBackgroundPosition = option?.contentBackgroundPosition ?? 'center';
  $: contentBackgroundRepeat = option?.contentBackgroundRepeat ?? 'no-repeat';

  // ===== SIZES =====
  $: mainSizes = option?.mainSizes || [{ size: 5 }, { size: 90 }, { size: 5 }];

  $: LEFT_SIZE = option?.leftSize ?? 27;
  $: RIGHT_SIZE = option?.rightSize ?? 24;
  $: LEFT_MIN = option?.leftMin ?? Math.min(LEFT_SIZE, 24);
  $: LEFT_MAX = option?.leftMax ?? 32;
  $: RIGHT_MIN = option?.rightMin ?? 0;
  $: RIGHT_MAX = option?.rightMax ?? 32;

  // ⭐ BODY SIZES (reactive theo right panel)
  $: bodySizes = $rightVisible
    ? [
        { size: LEFT_SIZE, min: LEFT_MIN, max: LEFT_MAX },
        { size: Math.max(0, 100 - LEFT_SIZE - RIGHT_SIZE) },
        { size: RIGHT_SIZE, min: RIGHT_MIN, max: RIGHT_MAX }
      ]
    : [{ size: LEFT_SIZE, min: LEFT_MIN, max: LEFT_MAX }, { size: 100 - LEFT_SIZE }, { size: 0, min: 0, max: 0 }];

  $: contentSizes = option?.contentSizes || [{ size: 5 }, { size: 85 }, { size: 10 }];

  $: layoutStyle = `
    --zen-gap-color: ${gapColor};
    --zen-divider-color: ${dividerColor};
    --zen-divider-hover-color: ${dividerHoverColor};
    --zen-divider-size: ${dividerSize};
    --zen-splitter-hit-size: ${splitterHitSize};
    --zen-divider-cursor-horizontal: ${horizontalCursor};
    --zen-divider-cursor-vertical: ${verticalCursor};
    --zen-grip-color: ${dividerColor};
    --zen-grip-hover-color: ${dividerHoverColor};
    --zen-pane-background: ${paneBackground};
    --zen-pane-border-color: ${paneBorderColor};
    --zen-pane-border-width: ${paneBorderWidth};
    --messenger-content-background: ${contentBackground};
    --messenger-content-background-size: ${contentBackgroundSize};
    --messenger-content-background-position: ${contentBackgroundPosition};
    --messenger-content-background-repeat: ${contentBackgroundRepeat};
  `;
</script>

<div class="desktop-layout" style={layoutStyle}>
  <!-- ===== MAIN SPLIT ===== -->
  <ZenSplit
    noGrip={mainNoGrip}
    mode={mainMode}
    lock={mainLock}
    persistLayout={layoutPersist}
    {gripPosition}
    {splitTheme}
    key={mainKey}
    horizontal={true}
    defaultSizes={mainSizes}
    let:paneSizes={paneSizesMain}>
    <!-- HEADER -->
    <ZenPane index={0} paneSizes={paneSizesMain} class="desktop-layout-slot desktop-layout-slot--header">
      <slot name="header" />
    </ZenPane>

    <!-- BODY -->
    <ZenPane index={1} paneSizes={paneSizesMain} class="desktop-layout-container desktop-layout-container--body">
      <ZenSplit
        noGrip={bodyNoGrip}
        mode={bodyMode}
        lock={bodyLock}
        persistLayout={layoutPersist}
        {gripPosition}
        {splitTheme}
        key={bodyKey}
        horizontal={false}
        defaultSizes={bodySizes}
        let:paneSizes={paneSizesBody}>
        <!-- LEFT -->
        <ZenPane index={0} paneSizes={paneSizesBody} class="desktop-layout-slot desktop-layout-slot--left">
          <slot name="left" />
        </ZenPane>

        <!-- CENTER -->
        <ZenPane index={1} paneSizes={paneSizesBody} class="desktop-layout-container desktop-layout-container--center">
          <ZenSplit
            noGrip={contentNoGrip}
            mode={contentMode}
            lock={contentLock}
            persistLayout={layoutPersist}
            {gripPosition}
            {splitTheme}
            key={contentKey}
            horizontal={true}
            defaultSizes={contentSizes}
            let:paneSizes={paneSizesContent}>
            <ZenPane index={0} paneSizes={paneSizesContent} class="desktop-layout-slot desktop-layout-slot--head">
              <slot name="head" />
            </ZenPane>

            <ZenPane index={1} paneSizes={paneSizesContent} class="desktop-layout-slot desktop-layout-slot--content">
              <slot name="content" />
            </ZenPane>

            <ZenPane index={2} paneSizes={paneSizesContent} class="desktop-layout-slot desktop-layout-slot--comment">
              <slot name="comment" />
            </ZenPane>
          </ZenSplit>
        </ZenPane>

        <!-- RIGHT -->
        <ZenPane index={2} paneSizes={paneSizesBody} class="desktop-layout-slot desktop-layout-slot--right">
          <slot name="right" />
        </ZenPane>
      </ZenSplit>
    </ZenPane>

    <!-- FOOTER -->
    <ZenPane index={2} paneSizes={paneSizesMain} class="desktop-layout-slot desktop-layout-slot--footer">
      <slot name="footer" />
    </ZenPane>
  </ZenSplit>
</div>

<style>
  .desktop-layout {
    isolation: isolate;
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 0;
    z-index: 0;
    background: var(--zen-gap-color, var(--zen-theme-gap-color, transparent));
  }

  :global(.desktop-layout .splitpanes__pane) {
    min-width: 0;
    min-height: 0;
    overflow: hidden;
    background: var(--zen-pane-background, transparent);
    border: 0;
    background-clip: padding-box;
    box-sizing: border-box;
  }

  :global(.desktop-layout .desktop-layout-slot) {
    isolation: isolate;
    position: relative;
  }

  :global(.desktop-layout .desktop-layout-slot--content) {
    background: var(--messenger-content-background);
    background-size: var(--messenger-content-background-size, cover);
    background-position: var(--messenger-content-background-position, center);
    background-repeat: var(--messenger-content-background-repeat, no-repeat);
  }

  :global(.desktop-layout .desktop-layout-slot::after) {
    content: "";
    position: absolute;
    inset: 0;
    z-index: 1;
    pointer-events: none;
    border: var(--zen-pane-border-width, 0) solid var(--zen-pane-border-color, var(--zen-theme-pane-border-color, transparent));
    box-sizing: border-box;
  }

  :global(.desktop-layout .zen-split) {
    width: 100%;
    height: 100%;
    min-height: 0;
  }

  :global(.desktop-layout .zen-split.splitpanes--horizontal > .splitpanes__splitter) {
    height: var(--zen-divider-size, 1px);
  }

  :global(.desktop-layout .zen-split.splitpanes--vertical > .splitpanes__splitter) {
    width: var(--zen-divider-size, 1px);
  }

  :global(.desktop-layout .zen-split--resizable.splitpanes--horizontal > .splitpanes__splitter) {
    height: var(--zen-splitter-hit-size, 8px);
  }

  :global(.desktop-layout .zen-split--resizable.splitpanes--vertical > .splitpanes__splitter) {
    width: var(--zen-splitter-hit-size, 8px);
  }

  :global(.desktop-layout .splitpanes__splitter) {
    background-color: var(--zen-gap-color, var(--zen-theme-gap-color, rgba(226, 232, 240, 0.95))) !important;
    transition:
      background-color 150ms ease,
      opacity 150ms ease;
  }

  :global(.desktop-layout .splitpanes__splitter:hover) {
    background-color: var(--zen-divider-hover-color, var(--zen-theme-divider-hover-color, rgba(148, 163, 184, 0.6))) !important;
  }

  :global(.desktop-layout .splitpanes__splitter::after) {
    background: transparent;
  }
</style>
